/**
 * ZugangPanel — Kundenportal-Zugang eines Kontakts.
 *
 * Kein Klartext-Passwort mehr: Admin legt einen Zugang an und teilt einen
 * persönlichen Link; der Kunde vergibt sein Passwort selbst (Supabase Auth).
 * Agenten sehen den Status, verwaltet wird er von Admins.
 *
 * @param {object} props.lead
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Info } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/api/AuthContext';
import { initSupabase } from '@/api/adapters/SupabaseAdapter';
import { isCustomerStatus } from '@/lib/leadStatus';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { base44 } from '@/api/base44Client';
import { InviteForm, LinkShare, PortalStatusBadge, PortalUserActions } from '@/Components/kundenportal/PortalAccess';

function usePortalUsersForLead(leadId) {
  return useQuery({
    queryKey: ['portal-users', 'lead', leadId],
    enabled: !!leadId,
    queryFn: async () => {
      const { data, error } = await initSupabase()
        .from('portal_users')
        .select('id, email, status, invited_at, last_login_at, lead_id')
        .eq('lead_id', leadId)
        .order('created_at', { ascending: true });
      if (error) {
        // Migration noch nicht ausgeführt → Tabelle fehlt
        if (error.code === '42P01' || /portal_users/.test(error.message)) return null;
        throw error;
      }
      return data || [];
    },
  });
}

export default function ZugangPanel({ lead }) {
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const { data: users, isLoading, error } = usePortalUsersForLead(lead?.id);
  const [neu, setNeu] = useState(null);
  const [statusNeu, setStatusNeu] = useState(null);
  const [markiere, setMarkiere] = useState(false);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['portal-users'] });

  if (!lead) return null;
  const kunde = isCustomerStatus(statusNeu || lead.status);

  const alsKundeMarkieren = async () => {
    setMarkiere(true);
    try {
      await base44.entities.Lead.update(lead.id, { status: 'Kunde' });
      setStatusNeu('Kunde');
      queryClient.invalidateQueries();
      toast.success(`${lead.name} ist jetzt „Kunde“`);
    } catch (err) {
      toast.error(`Status konnte nicht geändert werden: ${err.message}`);
    } finally {
      setMarkiere(false);
    }
  };

  if (isLoading) return <p className="text-sm text-slate-500 p-4">Lädt …</p>;
  if (error) return <p className="text-sm text-red-700 p-4">Zugänge konnten nicht geladen werden: {error.message}</p>;
  if (users === null) {
    return (
      <Card className="border-dashed"><CardContent className="p-6 text-sm text-slate-600">
        Das Kundenportal ist noch nicht eingerichtet (Datenbank-Migration fehlt). Siehe Admin → Kundenportal.
      </CardContent></Card>
    );
  }

  return (
    <div className="space-y-4">
      {users.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="p-6 text-center space-y-2">
            <KeyRound className="w-9 h-9 mx-auto text-slate-300" />
            <p className="text-sm text-slate-600">Für diesen Kontakt besteht noch kein Zugang zum Kundenportal.</p>
            {!kunde && <p className="text-xs text-slate-500">Ein Zugang ist erst ab Status „Kunde“ möglich.</p>}
            {!kunde && isAdmin && (
              <Button type="button" variant="outline" onClick={alsKundeMarkieren} disabled={markiere} className="mt-2">
                {markiere ? 'Wird gespeichert …' : 'Als „Kunde“ markieren'}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {users.map((u) => (
        <Card key={u.id}>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900 break-all">{u.email}</p>
                <p className="text-xs text-slate-500">
                  {u.last_login_at ? `Zuletzt angemeldet ${new Date(u.last_login_at).toLocaleString('de-DE')}` : 'Noch nie angemeldet'}
                </p>
              </div>
              <PortalStatusBadge status={u.status} />
            </div>
            {isAdmin && <PortalUserActions user={{ ...u, lead_name: lead.name }} telefon={lead.telefon} onChanged={refresh} />}
          </CardContent>
        </Card>
      ))}

      {neu && <LinkShare link={neu.link} email={neu.email} name={lead.ansprechpartner || lead.name} telefon={lead.telefon} onDone={() => setNeu(null)} />}

      {isAdmin && kunde && (
        <div className="space-y-2">
          <p className="text-xs font-bold text-slate-400 uppercase">{users.length ? 'Weiteren Login anlegen' : 'Zugang anlegen'}</p>
          <InviteForm lead={lead} onInvited={(res) => { setNeu(res); refresh(); }} />
        </div>
      )}

      {!isAdmin && (
        <p className="flex items-start gap-2 text-xs text-slate-500"><Info className="w-4 h-4 shrink-0" />Portalzugänge werden von der Administration verwaltet.</p>
      )}
    </div>
  );
}
